import React, { useState } from 'react';
import {
    Box,
    Button,
    TextField,
    Typography,
    Paper,
    Divider,
    Alert,
    InputAdornment,
    IconButton,
    Link,
    CircularProgress,
} from '@mui/material';
import {
    Visibility,
    VisibilityOff,
    Google as GoogleIcon,
    Email as EmailIcon,
    Lock as LockIcon,
    Person as PersonIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
const Signup = () => {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: '',
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isGoogleLoading, setIsGoogleLoading] = useState(false);
    const navigate = useNavigate();

    const handleInputChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData(prev => ({ ...prev, [field]: e.target.value }));
        setErrorMsg(''); // Clear error when user starts typing
    };

    const validateForm = () => {
        if (!formData.firstName.trim()) {
            setErrorMsg('First name is required');
            return false;
        }
        if (!formData.lastName.trim()) {
            setErrorMsg('Last name is required');
            return false;
        }
        if (!formData.email.trim()) {
            setErrorMsg('Email is required');
            return false;
        }
        if (!/\S+@\S+\.\S+/.test(formData.email)) {
            setErrorMsg('Please enter a valid email address');
            return false;
        }
        if (formData.password.length < 6) {
            setErrorMsg('Password must be at least 6 characters long');
            return false;
        }
        if (formData.password !== formData.confirmPassword) {
            setErrorMsg('Passwords do not match');
            return false;
        }
        return true;
    };

    const handleEmailSignup = async () => {
        if (!validateForm()) return;

        setIsLoading(true);
        setErrorMsg('');

        try {
            const { data, error } = await supabase.auth.signUp({
                email: formData.email,
                password: formData.password,
                options: {
                    data: {
                        first_name: formData.firstName,
                        last_name: formData.lastName,
                        full_name: `${formData.firstName} ${formData.lastName}`,
                        role: 'user', // Set default role to 'user'
                    },
                },
            });

            if (error) {
                setErrorMsg(error.message);
            } else if (data.user && !data.session) {
                setSuccessMsg('Please check your email to confirm your account before signing in.');
            } else if (data.user && data.session) {
                setSuccessMsg('Account created successfully! Redirecting...');
                setTimeout(() => navigate('/'), 1500);
            } else {
                setSuccessMsg('Account created successfully!');
                setTimeout(() => navigate('/'), 1500);
            }
        } catch (error: any) {
            setErrorMsg('An unexpected error occurred. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleGoogleSignup = async () => {
        setIsGoogleLoading(true);
        setErrorMsg('');

        try {
            const { error } = await supabase.auth.signInWithOAuth({
                provider: 'google',
                options: {
                    redirectTo: `${window.location.origin}/`,
                },
            });

            if (error) {
                setErrorMsg(error.message);
                setIsGoogleLoading(false);
            }
        } catch (error: any) {
            setErrorMsg('Failed to sign up with Google. Please try again.');
            setIsGoogleLoading(false);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleEmailSignup();
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'white',
            }}
        >
            <Paper
                elevation={24}
                sx={{
                    p: 4,
                    width: '100%',
                    maxWidth: 450,
                    borderRadius: 3,
                    backdropFilter: 'blur(10px)',
                }}
            >
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Typography variant="h4" fontWeight="bold" color="primary" gutterBottom>
                        Create Account
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Join loadoc and start managing your documents
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        You can create or join accounts after signing up
                    </Typography>
                </Box>

                {/* Google Signup Button */}
                <Button
                    variant="outlined"
                    fullWidth
                    size="large"
                    startIcon={isGoogleLoading ? <CircularProgress size={20} /> : <GoogleIcon />}
                    onClick={handleGoogleSignup}
                    disabled={isGoogleLoading || isLoading}
                    sx={{
                        mb: 2,
                        py: 1.5,
                        borderColor: '#db4437',
                        color: '#db4437',
                        '&:hover': {
                            borderColor: '#c23321',
                            backgroundColor: 'rgba(219, 68, 55, 0.04)',
                        },
                    }}
                >
                    {isGoogleLoading ? 'Signing up...' : 'Continue with Google'}
                </Button>

                <Divider sx={{ my: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                        or continue with email
                    </Typography>
                </Divider>

                {/* Name Fields */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <TextField
                        fullWidth
                        label="First Name"
                        value={formData.firstName}
                        onChange={handleInputChange('firstName')}
                        onKeyPress={handleKeyPress}
                        disabled={isLoading}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <PersonIcon color="action" />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <TextField
                        fullWidth
                        label="Last Name"
                        value={formData.lastName}
                        onChange={handleInputChange('lastName')}
                        onKeyPress={handleKeyPress}
                        disabled={isLoading}
                    />
                </Box>



                {/* Email Field */}
                <TextField
                    fullWidth
                    margin="normal"
                    label="Email Address"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange('email')}
                    onKeyPress={handleKeyPress}
                    disabled={isLoading}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <EmailIcon color="action" />
                            </InputAdornment>
                        ),
                    }}
                />

                {/* Password Field */}
                <TextField
                    fullWidth
                    margin="normal"
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleInputChange('password')}
                    onKeyPress={handleKeyPress}
                    disabled={isLoading}
                    helperText="Password must be at least 6 characters"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <LockIcon color="action" />
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    onClick={() => setShowPassword(!showPassword)}
                                    edge="end"
                                    disabled={isLoading}
                                >
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />

                {/* Confirm Password Field */}
                <TextField
                    fullWidth
                    margin="normal"
                    label="Confirm Password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={handleInputChange('confirmPassword')}
                    onKeyPress={handleKeyPress}
                    disabled={isLoading}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <LockIcon color="action" />
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    edge="end"
                                    disabled={isLoading}
                                >
                                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />

                {/* Error/Success Messages */}
                {errorMsg && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {errorMsg}
                    </Alert>
                )}
                {successMsg && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                        {successMsg}
                    </Alert>
                )}

                {/* Submit Button */}
                <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    onClick={handleEmailSignup}
                    disabled={isLoading || isGoogleLoading}
                    sx={{
                        mt: 3,
                        py: 1.5,
                        fontSize: '1.1rem',
                        fontWeight: 'bold',
                    }}
                >
                    {isLoading ? (
                        <>
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                            Creating Account...
                        </>
                    ) : (
                        'Create Account'
                    )}
                </Button>

                {/* Login Link */}
                <Box sx={{ textAlign: 'center', mt: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                        Already have an account?{' '}
                        <Link
                            component="button"
                            variant="body2"
                            onClick={() => navigate('/login')}
                            sx={{
                                textDecoration: 'none',
                                color: 'primary.main',
                                fontWeight: 'bold',
                                '&:hover': {
                                    textDecoration: 'underline',
                                },
                            }}
                        >
                            Sign in here
                        </Link>
                    </Typography>
                </Box>
            </Paper>
        </Box>
    );
};

export default Signup; 